import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// import OverviewView from './components/OverviewView';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import Modal from '../../components/common/Modal';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';

// Map frontend column keys to backend sort property names (Spring sort param)
const PROPOSALS_SORT_KEY_TO_BACKEND = {
  proposal: 'proposalCode',
  client: 'companyName',
  proposalDate: 'proposalDate',
  validUntil: 'validUntil',
  statusDesc: 'statusDesc',
  statusProposalDesc: 'statusProposalDesc',
  amount: 'grandTotal',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  createdBy: 'createdBy',
  assignedTo: 'assignedTo',
  department: 'department',
  sector: 'sectorName',
  projectStatus: 'projectStatusDesc',
  stageUpdatedAt: 'stageUpdatedAt',
  tagging: 'companyTagging',
  partnerType: 'partnerType',
  approver: 'approver',
  proposalStage: 'proposalStageId',
};

/** Normalize backend projectStatusDesc to "Converted" | "Not Converted" */
function normalizeProjectStatus(raw) {
  if (raw == null || String(raw).trim() === '') return 'Not Converted';
  const val = String(raw).toLowerCase();
  if (['converted', '1', 'true', 'done', 'closed', 'success'].includes(val)) return 'Converted';
  if (['not converted', '0', 'false', 'open', 'pending', 'draft'].includes(val)) return 'Not Converted';
  return val ? String(raw).charAt(0).toUpperCase() + String(raw).slice(1).toLowerCase() : 'Not Converted';
}

/** Convert the original proposal total to IDR using its creation-time rate. */
const getProposalAmountInIdr = (item) => {
  const sourceAmount = Number(item.grandTotalDisplayCurrency ?? item.grandTotal ?? 0);
  if (!Number.isFinite(sourceAmount)) return 0;

  const currencyCode = String(item.displayCurrencyCode || 'IDR').trim().toUpperCase();
  if (currencyCode === 'IDR') return sourceAmount;

  const creationRate = Number(item.exchangeRateAtCreation);
  return Number.isFinite(creationRate) && creationRate > 0
    ? sourceAmount * creationRate
    : sourceAmount;
};

const getEmptyProposalFilters = () => ({
  period: '',
  startDate: '',
  endDate: '',
  assignedTo: [],
  createdBy: [],
  department: [],
  sector: '',
  standards: [],
  service: [],
  source: '',
  proposalStage: [],
  approvalStatus: '',
  proposalStatus: '',
  projectStatus: '',
  branchId: [],
  taggingIds: [],
  proposalDateFrom: '',
  proposalDateTo: '',
  validUntilFrom: '',
  validUntilTo: '',
  stageUpdateFrom: '',
  stageUpdateTo: '',
  dateFrom: '',
  dateTo: '',
});

const Proposals = () => {
  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters('proposals');
  const [searchQuery, setSearchQuery] = useState('');
  /** Search term last applied (Enter or Filter Apply); keeps server search in sync when paging/sorting. */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterData, setFilterData] = useState(getEmptyProposalFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyProposalFilters);

  const [filterOptions, setFilterOptions] = useState({
    createdByUsers: [],
    assignedToUsers: [],
    stages: [],
    sectors: [],
    sources: [],
    departments: [],
    proposalStages: [],
    approvalStatuses: [
      { id: 1, name: 'Draft' },
      { id: 2, name: 'Approved' },
      { id: 3, name: 'Need Approval' },
      { id: 4, name: 'Rejected' },
      { id: 5, name: 'Revision' }
    ],
    proposalStatuses: [
      { id: 1, name: 'Draft' },
      { id: 2, name: 'Send to Client' },
      { id: 3, name: 'Accepted' },
      { id: 4, name: 'Rejected' },
      { id: 5, name: 'Feedback' }
    ],
    projectStatuses: [
      { id: 'converted', name: 'Converted' },
      { id: 'not converted', name: 'Not Converted' }
    ],
    standards: [],
    services: []
  });

  // Fallback options when API fails
  const fallbackOptions = {
    createdByUsers: [],
    assignedToUsers: [],
    stages: [],
    sectors: [],
    sources: [],
    departments: [],
    proposalStages: [],
    approvalStatuses: [
      { id: 1, name: 'Draft' },
      { id: 2, name: 'Approved' },
      { id: 3, name: 'Need Approval' },
      { id: 4, name: 'Rejected' },
      { id: 5, name: 'Revision' }
    ],
    proposalStatuses: [
      { id: 1, name: 'Draft' },
      { id: 2, name: 'Send to Client' },
      { id: 3, name: 'Accepted' },
      { id: 4, name: 'Rejected' },
      { id: 5, name: 'Feedback' }
    ],
    projectStatuses: [
      { id: 'converted', name: 'Converted' },
      { id: 'not converted', name: 'Not Converted' }
    ],
    standards: [],
    services: []
  };

  const [taggingOptions, setTaggingOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [proposalTaggingOptions, setProposalTaggingOptions] = useState([]);
  const [allUsersWithDept, setAllUsersWithDept] = useState([]);

  // Tambahkan state untuk data proposal
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [itemToConvert, setItemToConvert] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [showNpwpValidationModal, setShowNpwpValidationModal] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [npwpValidationCompanyId, setNpwpValidationCompanyId] = useState(null);
  const [npwpInputValue, setNpwpInputValue] = useState('');
  const [isSavingNpwp, setIsSavingNpwp] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalElements: 0,
    totalRows: 0,
    rowsPerPage: 10
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [totalSums, setTotalSums] = useState({
    totalAmount: 0,
    pageTotal: 0
  });
  const [proposalCurrencyCode, setProposalCurrencyCode] = useState('IDR');

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();

  // Returns the IDR-equivalent amount for a proposal.
  // Prefers grandTotalBaseCurrency (pre-calculated IDR) to avoid unreliable
  // exchangeRateAtCreation fallback-to-1 inflation on non-IDR proposals.
  const proposalBaseAmount = (proposal) =>
    parseFloat(proposal.amount) ||
    parseFloat(proposal.grandTotalBaseCurrency) ||
    parseFloat(proposal.grandTotal) ||
    0;

  // ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency symbol
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'JPY': return '¥';
      case 'SGD': return 'S$';
      default: return 'Rp';
    }
  };

  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '';
  
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
  
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
  
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };
  

  // ✅ MULTI-CURRENCY: Helper function untuk format amount dengan currency
  const formatAmount = (amount, currencyCode = 'IDR') => {
    if (!amount || amount === 0) {
      return `${getCurrencySymbol(currencyCode)} 0`;
    }

    const symbol = getCurrencySymbol(currencyCode);
    const formattedAmount = amount.toLocaleString('id-ID');
    return `${symbol} ${formattedAmount}`;
  };

  // ✅ TOTALS: Helper function untuk format currency (similar to Invoice)
  const formatCurrency = (amount, currencyCode = proposalCurrencyCode) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  // Fetch company data by companyId
  const fetchCompanyData = async (companyId) => {
    try {
      const response = await api.get(`/crm/companies/${companyId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching company data:', error);
      return null;
    }
  };

  // Allow filling in NPWP directly from the "Cannot Convert to Project" modal
  const handleSaveNpwpAndContinue = async () => {
    const trimmedNpwp = npwpInputValue.trim();
    if (!trimmedNpwp) {
      showAlert('warning', 'Please enter a Company Tax Number (NPWP) before continuing.');
      return;
    }
    if (!npwpValidationCompanyId) {
      showAlert('danger', 'Cannot save NPWP: Company could not be identified.');
      return;
    }

    setIsSavingNpwp(true);
    try {
      // Fetch the same shape the real Company edit form uses (/md/companies/{id} returns
      // { company, contacts } separately) — PUTting a company object built from a different
      // endpoint's shape makes the backend think contacts were removed and blocks it with an
      // FK constraint error.
      const detailRes = await api.get(`/md/companies/${npwpValidationCompanyId}`);
      const freshCompany = detailRes.data?.company ?? {};
      const contacts = Array.isArray(detailRes.data?.contacts) ? detailRes.data.contacts : [];
      const taggingIds = Array.isArray(freshCompany.taggingIds)
        ? freshCompany.taggingIds.map((tid) => Number(tid)).filter((n) => !isNaN(n))
        : [];

      const updatedCompany = { ...freshCompany, contacts, taggingIds, npwp: trimmedNpwp };
      await api.put(`/md/companies/${npwpValidationCompanyId}`, updatedCompany);

      setCompanyData(updatedCompany);
      setShowNpwpValidationModal(false);
      showAlert('success', 'Company Tax Number (NPWP) saved successfully.');
      // Proceed straight to the conversion confirmation
      setShowConvertModal(true);
    } catch (error) {
      console.error('Error saving NPWP:', error);
      showAlert('danger', 'Failed to save NPWP: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSavingNpwp(false);
    }
  };

  // Modal cleanup is handled by the modal components themselves
  // Removed problematic DOM manipulation that interfered with React's modal lifecycle

  // Helper: convert array filter values to comma-separated string for API
  const toFilterParam = (val) => {
    if (Array.isArray(val) && val.length > 0) return val.join(',');
    if (val && !Array.isArray(val)) return val;
    return undefined;
  };

  const getProposalListFilters = (filters = appliedFilters, search = appliedSearch) => {
    const q = (search ?? '').trim();
    const { startDate, endDate } = getActivePeriodRange(filters);
    const base = {};

    // Period filter uses stage_updated_at to match Sales Funnel pipeline logic
    if (startDate) base.stageUpdateFrom = startDate;
    else if (filters.dateFrom) base.stageUpdateFrom = filters.dateFrom;
    if (endDate) base.stageUpdateTo = endDate;
    else if (filters.dateTo) base.stageUpdateTo = filters.dateTo;

    const arrayFields = ['assignedTo', 'createdBy', 'department', 'standards', 'service', 'proposalStage', 'branchId'];
    arrayFields.forEach((key) => {
      const param = toFilterParam(filters[key]);
      if (param) base[key] = param;
    });

    const scalarFields = ['sector', 'source', 'approvalStatus', 'proposalStatus', 'projectStatus',
      'proposalDateFrom', 'proposalDateTo', 'validUntilFrom', 'validUntilTo',
      'stageUpdateFrom', 'stageUpdateTo'];
    scalarFields.forEach((key) => {
      if (filters[key]) base[key] = filters[key];
    });

    // taggingIds is filtered client-side — excluded from API params

    if (q) base.search = q;
    return base;
  };

  // Fetch proposals with filters (backend returns Page<T>)
  const fetchProposals = async (filters = {}, taggingFilter = [], pageNum = page, pageSize = size, sortConfigOverride = null) => {
    setLoading(true);
    try {
      // Tagging filter: company tags ARE filterable server-side via the `taggingId` param
      // (backend has no equivalent for proposal-specific tags). Splitting here decides whether
      // we can let the backend paginate/count correctly, or must fall back to a full client-side
      // pass — see the branching below `params` for why.
      const effectiveTaggingIds = taggingFilter.length > 0 ? taggingFilter : [];
      const companyTagIds = effectiveTaggingIds.filter((id) => !String(id).startsWith('proposal-'));
      const proposalTagIds = effectiveTaggingIds.filter((id) => String(id).startsWith('proposal-'));
      // Only company tags selected → the backend can filter+paginate+count exactly, no client
      // pass needed. If ANY proposal tag is selected, sending `taggingId` would AND-restrict the
      // backend query to "has this company tag", which would silently drop proposals that only
      // match on the proposal tag — the two are meant to OR together. In that mixed/proposal-only
      // case we must fetch a full unpaginated set and filter+paginate client-side instead.
      const needsClientSideTaggingPass = proposalTagIds.length > 0;

      // Build query parameters for permission-based filtering and user filters
      const params = new URLSearchParams();
      if (needsClientSideTaggingPass) {
        // Fetch every row matching the other filters so the tagging OR-match + resulting count
        // are computed over the true full set, not just whatever page the backend would have
        // returned — we then paginate the filtered result ourselves below.
        params.set('page', '0');
        params.set('size', '100000');
      } else {
        params.set('page', String(pageNum));
        params.set('size', String(pageSize));
        if (companyTagIds.length > 0) params.set('taggingId', companyTagIds.join(','));
      }

      // ✅ Server-side sort: backend expects sort=propertyName,direction (e.g. sort=companyName,asc)
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && PROPOSALS_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = PROPOSALS_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.set('sort', `${backendSortProperty},${effectiveSort.direction}`);
      }

      // Add user-applied filters
      Object.keys(filters).forEach(key => {
        const rawVal = filters[key];
        if (rawVal != null && rawVal !== '') {
          const normalized = typeof rawVal === 'object' && 'value' in rawVal ? rawVal.value : rawVal;
          params.append(key, normalized);
        }
      });

      // ✅ Permission-based filtering hierarchy:
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by user's department
      // 3. view_own → filter by user ID (created by OR assigned to)
      // NOTE: Don't override if user explicitly selects filters

      if (!can('view_global')) {
        if (can('view_department')) {
          // Filter by department (unless user explicitly filters by createdBy/assignedTo)
          if (!params.has('createdBy') && !params.has('assignedTo') && user?.departmentId) {
            params.append('viewDepartmentId', user.departmentId);
          }
        } else if (can('view_own')) {
          // Filter by user (created by OR assigned to)
          if (!params.has('createdBy') && !params.has('assignedTo') && user?.id) {
            params.append('viewOwnUserId', user.id);
          }
        }
      }

      const url = `/crm/proposals?${params.toString()}`;
      const res = await api.get(url);
      const data = res.data;
      const rawList = Array.isArray(data) ? data : (data?.content ?? []);

      const currencyCodeCache = new Map();
      const mappedProposals = await Promise.all(rawList.map(async item => {
        let currencyCode = String(item.displayCurrencyCode || '').trim().toUpperCase();
        // Older list responses may include displayCurrencyId but omit the
        // joined currency code. Resolve it before deciding whether conversion
        // is needed; treating an unknown currency as IDR is unsafe.
        if (!currencyCode && item.displayCurrencyId) {
          const currencyId = String(item.displayCurrencyId);
          if (!currencyCodeCache.has(currencyId)) {
            currencyCodeCache.set(currencyId,
              api.get(`/crm/currencies/${item.displayCurrencyId}`)
                .then(response => String(response.data?.currencyCode || '').trim().toUpperCase())
                .catch(() => '')
            );
          }
          currencyCode = await currencyCodeCache.get(currencyId);
        }
        currencyCode = currencyCode || 'IDR';
        let effectiveRate = Number(item.exchangeRateAtCreation);
        // Use the exchange-rate snapshot stored on this proposal. Do not
        // resolve against the mutable currency-rate table.
        if (currencyCode === 'IDR') effectiveRate = 1;
        const amountItem = {
          ...item,
          displayCurrencyCode: currencyCode,
          ...(effectiveRate > 0 ? { exchangeRateAtCreation: effectiveRate } : {})
        };
        return {
        ...amountItem,
        proposal: item.proposalCode,
        client: item.companyName,
        // Amount is displayed in IDR using this proposal's saved rate.
        amount: getProposalAmountInIdr(amountItem),
        // ✅ PERFORMANCE FIX: companyId is now included in backend response
        companyId: item.companyId || null,
        // Fix case sensitivity for status descriptions
        // NOTE: Approver column rendering checks for 'Need Approval' (transformed value)
        statusDesc: item.statusDesc === 'APPROVED' ? 'Approved' :
          item.statusDesc === 'REJECTED' ? 'Rejected' :
            item.statusDesc === 'REVISION' ? 'Revision' :
              item.statusDesc === 'NEED_APPROVAL' ? 'Need Approval' :
                item.statusDesc || 'Draft',
        statusProposalDesc: item.statusProposalDesc === 'ACCEPTED' ? 'Accepted' :
          item.statusProposalDesc === 'REJECTED' ? 'Rejected' :
            item.statusProposalDesc === 'FEEDBACK' ? 'Feedback' :
              item.statusProposalDesc || 'Draft',
        // Add project status and proposal stage fields (backend already returns projectStatusDesc)
        projectStatus: normalizeProjectStatus(item.projectStatusDesc),
        // Simpan ID stage agar bisa dilookup namanya dari tabel proposal_stages
        proposalStageId: item.proposalStageId || item.proposalStage?.id || item.stageId || null,
        // Keep any backend-provided display name as fallback
        proposalStage: item.proposalStageName || item.proposalStage || null
        };
      }));

      // Backend proposal list API already includes projectStatusDesc; no status-by-leads call needed
      let enhancedProposals = mappedProposals.map(p => ({
        ...p,
        projectStatus: p.projectStatus || 'Not Converted'
      }));

      // ✅ PERFORMANCE FIX: companyId is now included in the backend response
      // No need to make individual API calls for each lead
      // The backend query already joins with leads and companies tables

      let displayProposals = enhancedProposals;

      if (needsClientSideTaggingPass) {
        // `enhancedProposals` here is the FULL matching set (see the page=0&size=100000 request
        // above), so filtering + counting it gives an accurate total instead of only reflecting
        // whatever page the backend happened to return.
        const fullMatch = enhancedProposals.filter(p => {
          const companyNames = p.tagging ? p.tagging.split(',').map(s => s.trim()).filter(Boolean) : [];
          const rowCompanyTagIds = companyNames.map(name => {
            const found = taggingOptions.find(opt => opt.name === name);
            return found ? String(found.id) : null;
          }).filter(Boolean);

          const proposalRaw = p.proposalTagging ?? p.proposal_tagging ?? '';
          const proposalNames = typeof proposalRaw === 'string' ? proposalRaw.split(',').map(s => s.trim()).filter(Boolean) : [];
          const rowProposalTagIds = proposalNames.map(name => {
            const found = proposalTaggingOptions.find(opt => opt.name === name);
            return found ? `proposal-${found.id}` : null;
          }).filter(Boolean);

          return effectiveTaggingIds.some(id => rowCompanyTagIds.includes(id) || rowProposalTagIds.includes(id));
        });

        const clientTotalElements = fullMatch.length;
        const clientTotalPages = Math.max(1, Math.ceil(clientTotalElements / pageSize));
        displayProposals = fullMatch.slice(pageNum * pageSize, (pageNum + 1) * pageSize);

        setProposals(enhancedProposals);
        setFilteredProposals(displayProposals);
        setTotalElements(clientTotalElements);
        setTotalPages(clientTotalPages);
        setPagination(prev => ({
          ...prev,
          totalRows: clientTotalElements,
          totalPages: clientTotalPages,
          totalElements: clientTotalElements,
          rowsPerPage: pageSize,
          currentPage: pageNum + 1
        }));
        setTotalSums({
          totalAmount: fullMatch.reduce((acc, p) => acc + proposalBaseAmount(p), 0),
          pageTotal: displayProposals.reduce((acc, p) => acc + proposalBaseAmount(p), 0)
        });
        return;
      }

      // No proposal-tag filtering needed — the backend already applied `taggingId` (if any
      // company tags were selected) and paginated/counted correctly, so no client pass required.

      setProposals(enhancedProposals);
      setFilteredProposals(displayProposals);
      if (data && typeof data.totalElements === 'number') {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
        setPagination(prev => ({
          ...prev,
          totalRows: data.totalElements,
          totalPages: data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)),
          totalElements: data.totalElements,
          rowsPerPage: pageSize,
          currentPage: pageNum + 1
        }));
      } else {
        updatePaginationTotals(displayProposals);
      }

      // Total amount: use backend-computed totals when present.
      // totalAmount = sum across ALL filtered proposals (global).
      // pageTotal   = sum for THIS page only (no client-side reduce needed).
      const backendTotal = data?.totalAmount != null ? Number(data.totalAmount) : null;
      const backendPageTotal = data?.pageTotal != null ? Number(data.pageTotal) : null;
      setTotalSums({
        totalAmount: backendTotal ?? displayProposals.reduce((acc, p) => acc + proposalBaseAmount(p), 0),
        pageTotal: backendPageTotal ?? displayProposals.reduce((acc, p) => acc + proposalBaseAmount(p), 0)
      });
    } catch (err) {
      console.error('Error fetching proposals:', err);

      // ✅ TAMBAHAN: Enhanced error handling untuk memberikan pesan yang lebih informatif
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load proposals data';

      console.error('Detailed fetch proposals error:', {
        message: errorMessage,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        fullError: err
      });

      showAlert('warning', `Load proposals failed: ${errorMessage}`);
      setProposals([]);
      setFilteredProposals([]);
      setTotalElements(0);
      setTotalPages(0);
      setTotalSums({ totalAmount: 0, pageTotal: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data proposal dari API (initial load)
  useEffect(() => {
    if (!capabilitiesLoading) {
      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? getEmptyProposalFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? 10;

        setAppliedFilters(restoredFilters);
        setFilterData(restoredFilters);
        setAppliedSearch(restoredSearch);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);
        setPagination(prev => ({ ...prev, currentPage: restoredPage + 1, rowsPerPage: restoredSize }));

        const taggingIds = Array.isArray(restoredFilters.taggingIds) ? restoredFilters.taggingIds : [];
        fetchProposals(getProposalListFilters(restoredFilters, restoredSearch), taggingIds, restoredPage, restoredSize, sortConfig);
      } else {
        fetchProposals(getProposalListFilters(), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], 0, size, sortConfig);
      }
    }
  }, [capabilitiesLoading]);

  // Fetch filter options from API (branch filter shows only current user's branches)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [filterRes, taggingRes, proposalTaggingRes, userBranchesRes, sourcesRes, standardsRes, departmentsRes, sectorsRes, proposalStagesRes, internalUsersRes, servicesRes] = await Promise.all([
          api.get('/crm/proposals/filter-options').catch(() => ({ data: { createdByUsers: [], assignedToUsers: [] } })),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }),
          api.get('/md/tagging', { params: { moduleKey: 'proposal' } }),
          user?.id ? api.get(`/user-branches/user/${user.id}`) : Promise.resolve({ data: { data: [] } }),
          api.get('/dashboard/sources').catch(() => ({ data: { sources: [] } })),
          api.get('/md/standards').catch(() => ({ data: [] })),
          api.get('/md/departement').catch(() => ({ data: [] })),
          api.get('/md/sector').catch(() => ({ data: [] })),
          api.get('/md/proposal-status').catch(() => ({ data: [] })),
          api.get('/md/internal-users/option-internal-user').catch(() => ({ data: [] })),
          api.get('/crm/services/options').catch(() => ({ data: [] }))
        ]);

        setTaggingOptions(taggingRes.data || []);
        setProposalTaggingOptions(proposalTaggingRes.data || []);
        setAllUsersWithDept(internalUsersRes.data || []);
        const branchData = userBranchesRes.data?.data ?? userBranchesRes.data ?? [];
        setBranches(Array.isArray(branchData) ? branchData : []);

        const toIdName = (item) => ({ id: item.id, name: item.name });
        const newFilterOptions = {
          ...filterOptions,
          createdByUsers: filterRes.data.createdByUsers || [],
          departments: (departmentsRes.data || []).map(toIdName),
          assignedToUsers: filterRes.data.assignedToUsers || [],
          sectors: (sectorsRes.data || []).map(toIdName),
          proposalStages: (filterRes.data.proposalStages || []).map(toIdName),
          standards: (standardsRes.data || []).map(s => ({ id: s.id, name: s.name })),
          services: (servicesRes.data || []).map(s => ({ id: s.id ?? s.value, name: s.name ?? s.label })),
          sources: sourcesRes.data?.sources || []
        };

        setFilterOptions(newFilterOptions);
      } catch (err) {
        console.error('Error fetching filter options:', err);

        setFilterOptions(fallbackOptions);
        showAlert('warning', 'Using fallback filter options. API not available.');
      }
    };

    fetchFilterOptions();
  }, [user?.id]); // Refetch when user is available to load user's branches for filter



  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  // Handle search - Enter runs server-side search (partial match on proposal code & company name in API)
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setPage(0);
    const q = (searchQuery ?? '').trim();
    setAppliedSearch(q);
    persistState({ filters: appliedFilters, search: q, page: 0, size });
    fetchProposals(getProposalListFilters(appliedFilters, q), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], 0, size, sortConfig);
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilterReset = () => {
    const empty = getEmptyProposalFilters();
    setFilterData(empty);
    setAppliedFilters(empty);

    // Reset search query as well
    setSearchQuery('');
    setAppliedSearch('');

    // Reset pagination to page 1
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setPage(0);

    persistState({ filters: empty, search: '', page: 0, size });

    // Refetch with no filters so backend returns full list and totalAmount
    fetchProposals({}, [], 0, size, sortConfig);

    showAlert('info', 'Filter reset. Showing all proposals.');
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const committed = { ...filterData, startDate, endDate };
    setAppliedFilters(committed);
    setPage(0);
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    const q = (searchQuery ?? '').trim();
    setAppliedSearch(q);

    persistState({ filters: committed, search: q, page: 0, size });

    const taggingIds = Array.isArray(committed.taggingIds) ? committed.taggingIds : [];
    fetchProposals(getProposalListFilters(committed, q), taggingIds, 0, size, sortConfig);
    showAlert('success', 'Filter applied successfully.');
  };

  const hasActiveFilters = () => {
    return activeFilterCount > 0 || (searchQuery && searchQuery.trim() !== '');
  };

  // Build userId → { deptId, status } lookup from internal-users endpoint
  const userDeptLookup = useMemo(() => {
    const map = new Map();
    (allUsersWithDept || []).forEach(u => {
      const deptId = u.departmentId || u.departementId;
      if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
    });
    return map;
  }, [allUsersWithDept]);

  const withInactiveBadge = (userId, label) => {
    const entry = userDeptLookup.get(String(userId));
    return entry?.status === '0' ? `${label} (Inactive)` : label;
  };

  // Cascade: filter people options by selected department(s); mark inactive users
  const cascadedAssignedToOptions = useMemo(() => {
    const dept = filterData.department;
    const selectedDepts = Array.isArray(dept) ? dept.map(String).filter(Boolean) : dept ? [String(dept)] : [];
    const base = (filterOptions.assignedToUsers || []).map(u => ({ value: String(u.id), label: withInactiveBadge(u.id, u.name) }));
    if (!selectedDepts.length) return base;
    return base.filter(u => { const e = userDeptLookup.get(u.value); return e?.deptId != null && selectedDepts.includes(e.deptId); });
  }, [filterData.department, filterOptions.assignedToUsers, userDeptLookup]);

  const cascadedCreatedByOptions = useMemo(() => {
    return (filterOptions.createdByUsers || []).map(u => ({ value: String(u.id), label: withInactiveBadge(u.id, u.name) }));
  }, [filterOptions.createdByUsers]);

  // proposalFilterFields config for AdvancedFilterPanel
  const proposalFilterFields = useMemo(() => [
    { label: 'Proposal Date', type: 'dateRange', fromKey: 'proposalDateFrom', toKey: 'proposalDateTo' },
    { label: 'Valid Until', type: 'dateRange', fromKey: 'validUntilFrom', toKey: 'validUntilTo' },
    { label: 'Stage Update Date', type: 'dateRange', fromKey: 'stageUpdateFrom', toKey: 'stageUpdateTo' },
    { key: 'department', label: 'Department', type: 'multiselect', options: (filterOptions.departments || []).map(d => ({ value: String(d.id), label: d.name })) },
    { key: 'assignedTo', label: 'Assigned To', type: 'multiselect', options: cascadedAssignedToOptions },
    { key: 'createdBy', label: 'Created By', type: 'multiselect', options: cascadedCreatedByOptions },
    { key: 'sector', label: 'Sector', type: 'select', options: (filterOptions.sectors || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'standards', label: 'Standard', type: 'multiselect', options: (filterOptions.standards || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'service', label: 'Service', type: 'multiselect', options: (filterOptions.services || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'source', label: 'Source', type: 'select', options: (filterOptions.sources || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'proposalStage', label: 'Proposal Stage', type: 'multiselect', options: (filterOptions.proposalStages || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'approvalStatus', label: 'Approval Status', type: 'select', options: (filterOptions.approvalStatuses || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'proposalStatus', label: 'Proposal Status', type: 'select', options: (filterOptions.proposalStatuses || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'projectStatus', label: 'Project Status', type: 'select', options: (filterOptions.projectStatuses || []).map(s => ({ value: String(s.id), label: s.name })) },
    { key: 'branchId', label: 'Branch', type: 'multiselect', options: (branches || []).map(b => ({ value: String(b.id), label: b.name || String(b.id) })) },
    { key: 'taggingIds', label: 'Tagging', type: 'multiselect', options: [
        ...(taggingOptions || []).map(t => ({ value: String(t.id), label: `${t.name} (Company)` })),
        ...(proposalTaggingOptions || []).map(t => ({ value: `proposal-${t.id}`, label: `${t.name} (Proposal)` }))
      ].filter(o => o.value && o.label)
    },
  ], [filterOptions, branches, taggingOptions, proposalTaggingOptions, cascadedAssignedToOptions, cascadedCreatedByOptions]);

  const activeFilterCount = getActiveFilterCount(appliedFilters, proposalFilterFields);

  const proposalFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({ key: 'period', label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}` });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({ key: 'period', label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}` });
    } else if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      tags.push({ key: 'dateFromDateTo', label: `Created Date: ${appliedFilters.dateFrom || '...'} - ${appliedFilters.dateTo || '...'}` });
    }

    proposalFilterFields.forEach((field) => {
      if (field.type === 'dateRange') {
        if (appliedFilters[field.fromKey] || appliedFilters[field.toKey]) {
          tags.push({ key: `${field.fromKey}_${field.toKey}`, label: `${field.label}: ${appliedFilters[field.fromKey] || '...'} - ${appliedFilters[field.toKey] || '...'}` });
        }
        return;
      }
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find((opt) => String(opt.value) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option.label : value}` });
    });

    if (searchQuery && searchQuery.trim() !== '') {
      tags.push({ key: 'search', label: `Search: "${searchQuery.trim()}"` });
    }

    return tags;
  }, [appliedFilters, proposalFilterFields, searchQuery]);

  const getClearedProposalFilters = (filters, key) => {
    const nextFilters = { ...filters };
    if (['period', 'startDate', 'endDate', 'dateFromDateTo'].includes(key)) {
      nextFilters.period = '';
      nextFilters.startDate = '';
      nextFilters.endDate = '';
      nextFilters.dateFrom = '';
      nextFilters.dateTo = '';
    } else {
      // Check if key matches a dateRange field (fromKey_toKey pattern)
      const dateRangeField = proposalFilterFields.find(f => f.type === 'dateRange' && `${f.fromKey}_${f.toKey}` === key);
      if (dateRangeField) {
        nextFilters[dateRangeField.fromKey] = '';
        nextFilters[dateRangeField.toKey] = '';
      } else {
        const field = proposalFilterFields.find(f => f.key === key);
        nextFilters[key] = (field && (field.type === 'multiselect' || key === 'taggingIds')) ? [] : '';
      }
    }
    return nextFilters;
  };

  const handleProposalChipRemove = (key) => {
    if (key === 'search') {
      setSearchQuery('');
      setAppliedSearch('');
      fetchProposals(getProposalListFilters(appliedFilters, ''), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], 0, size, sortConfig);
      return;
    }

    const nextApplied = getClearedProposalFilters(appliedFilters, key);
    setFilterData(prev => getClearedProposalFilters(prev, key));
    setAppliedFilters(nextApplied);
    setPage(0);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    persistState({ filters: nextApplied, search: appliedSearch, page: 0, size });
    const taggingIds = Array.isArray(nextApplied.taggingIds) ? nextApplied.taggingIds : [];
    fetchProposals(getProposalListFilters(nextApplied, appliedSearch), taggingIds, 0, size, sortConfig);
  };

  // Pagination handlers (server-side: refetch with new page/size)
  const handlePageChange = (newPage) => {
    const nextPage = newPage - 1;
    setPage(nextPage);
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    persistState({ filters: appliedFilters, search: appliedSearch, page: nextPage, size });
    fetchProposals(getProposalListFilters(), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], nextPage, size, sortConfig);
  };

  const handleRowsPerPageChange = (newSize) => {
    setSize(newSize);
    setPage(0);
    setPagination(prev => ({ ...prev, rowsPerPage: newSize, currentPage: 1 }));
    persistState({ filters: appliedFilters, search: appliedSearch, page: 0, size: newSize });
    fetchProposals(getProposalListFilters(), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], 0, newSize, sortConfig);
  };

  // Sort handler: backend sort — refetch with new sort
  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPage(0);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchProposals(getProposalListFilters(), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], 0, size, newSortConfig);
  };

  // Get current page data from filtered proposals (with sorting applied)
  const getCurrentPageData = () => {
    let sortedData = [...filteredProposals];

    // When server-side sort is active (column mapped in backend), data from API is already sorted
    const useServerSort = sortConfig.key && PROPOSALS_SORT_KEY_TO_BACKEND[sortConfig.key];
    if (sortConfig.key && !useServerSort) {
      // Client-sort only for columns not supported by backend (e.g. tagging, partnerType, approver, proposalStage)
      sortedData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle special cases for sorting
        if (sortConfig.key === 'amount') {
          aValue = parseFloat(a.amount) || parseFloat(a.grandTotal) || 0;
          bValue = parseFloat(b.amount) || parseFloat(b.grandTotal) || 0;
        } else if (sortConfig.key === 'client') {
          aValue = a.client || a.companyName || '';
          bValue = b.client || b.companyName || '';
        } else if (sortConfig.key === 'createdBy') {
          aValue = `${a.createdBy?.firstName || ''} ${a.createdBy?.lastName || ''}`.trim();
          bValue = `${b.createdBy?.firstName || ''} ${b.createdBy?.lastName || ''}`.trim();
        } else if (sortConfig.key === 'assignedTo') {
          aValue = `${a.assignedTo?.firstName || ''} ${a.assignedTo?.lastName || ''}`.trim();
          bValue = `${b.assignedTo?.firstName || ''} ${b.assignedTo?.lastName || ''}`.trim();
        } else if (sortConfig.key === 'department') {
          aValue = a.department?.name || '';
          bValue = b.department?.name || '';
        } else if (sortConfig.key === 'proposalDate' || sortConfig.key === 'validUntil' ||
                   sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt' ||
                   sortConfig.key === 'stageUpdatedAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    // Then apply pagination
    const { currentPage, rowsPerPage } = pagination;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData.slice(startIndex, endIndex);
  };

  // Update pagination totals
  const updatePaginationTotals = (totalData) => {
    const totalRows = totalData.length;
    const totalPages = Math.ceil(totalRows / pagination.rowsPerPage);
    setPagination(prev => ({
      ...prev,
      totalPages,
      totalElements: totalRows,
      totalRows
    }));
  };

  // Totals (totalAmount) are set from backend in fetchProposals; no client-side recalc needed.

  // Stats data for overview
  const stats = {
    leads: {
      total: 56,
      new: 40,
      existing: 16
    },
    hot: {
      total: 37,
      new: 20,
      existing: 17
    },
    negotiation: {
      total: 12,
      new: 10,
      existing: 2
    },
    converted: {
      total: 56,
      new: 40,
      existing: 16
    }
  };

  const handleEditLead = (lead) => {
    if (!can('edit')) {
      showAlert('danger', 'You do not have permission to edit proposals.');
      return;
    }

    // Check if editing is allowed for the current status
    const isDraft = lead?.statusProposal === 1 || lead?.statusProposalDesc === 'Draft';
    const isRevision = lead?.statusDesc === 'Revision' || lead?.statusApproval === 'Revision';
    const isFeedback = lead?.statusProposal === 5 || lead?.statusProposalDesc === 'Feedback';

    // Only allow editing for draft, revision, and feedback statuses
    if (isDraft || isRevision || isFeedback) {
      navigate(`/proposals/edit/${lead.leadId}`);
      return;
    }

    // Show appropriate error message for other statuses
    let reason = 'Cannot edit: ';
    const statusDesc = lead?.statusProposalDesc || lead?.statusDesc || 'Unknown';

    if (lead?.statusApproval === 'Approved' || lead?.statusDesc === 'Approved') {
      reason += 'Proposal is already approved';
    } else if (lead?.statusProposal === 3 || lead?.statusProposalDesc === 'Accepted') {
      reason += 'Proposal is already accepted';
    } else if (lead?.statusProposal === 2 || lead?.statusProposalDesc === 'Send to Client' || lead?.statusProposalDesc === 'Send To Client') {
      reason += 'Proposal has been sent to client';
    } else if (lead?.statusDesc === 'Need Approval' || lead?.statusApproval === 'Need Approval') {
      reason += 'Proposal is waiting for approval';
    } else if (lead?.projectStatus === 'Converted') {
      reason += 'Proposal is already converted to project';
    } else {
      reason += `Current status is "${statusDesc}"`;
    }

    showAlert('warning', reason);
  };

  const handleDeleteLead = (lead) => {
    if (!can('delete')) {
      showAlert('danger', 'You do not have permission to delete proposals.');
      return;
    }

    setItemToDelete(lead);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/crm/proposals/${itemToDelete.id}`);
      const updatedProposals = proposals.filter(p => p.id !== itemToDelete.id);
      const updatedFilteredProposals = filteredProposals.filter(p => p.id !== itemToDelete.id);
      // Refetch current page so list and backend totalAmount stay in sync
      fetchProposals(getProposalListFilters(), Array.isArray(appliedFilters.taggingIds) ? appliedFilters.taggingIds : [], page, size, sortConfig);

      showAlert('success', `Proposal ${itemToDelete.proposal} deleted successfully`);
    } catch (err) {
      console.error('Delete error:', err);

      // ✅ TAMBAHAN: Enhanced error handling untuk memberikan pesan yang lebih informatif
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to delete proposal';

      console.error('Detailed delete error:', {
        message: errorMessage,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        fullError: err
      });

      showAlert('danger', `Delete failed: ${errorMessage}`);
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const getProposalStageName = (row) => {
    const id = row?.proposalStageId;
    const found = id != null ? (filterOptions?.proposalStages || []).find(ps => ps.id === parseInt(id)) : null;
    return found?.name || row.proposalStage || 'Not Set';
  };

  const handleExportExcel = () => {
    if (!can('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }

    try {
      let dataToExport = [];

      if (selectedRows && selectedRows.length > 0) {
        const selectedIds = selectedRows.map(id => String(id));
        dataToExport = filteredProposals.filter(row => selectedIds.includes(String(row.id)));
      } else {
        dataToExport = filteredProposals;
      }

      const columnExportMap = {
        proposal: { label: 'Proposal', value: (row) => row.proposal || '', isNumeric: false },
        client: { label: 'Client', value: (row) => row.client || row.companyName || '', isNumeric: false },
        proposalDate: {
          label: 'Proposal Date',
          value: (row) => formatDateDisplay(row.proposalDate),
          isNumeric: false
        },
        validUntil: {
          label: 'Valid Until',
          value: (row) => formatDateDisplay(row.validUntil),
          isNumeric: false
        },
        partnerType: { label: 'Partner Type', value: (row) => row.partnerType || '', isNumeric: false },
        sector: { label: 'Sector', value: (row) => row.sectorName || row.sector || '-', isNumeric: false },
        amount: {
          label: 'Amount',
          value: (row) => parseFloat(row.amount) || 0,
          isNumeric: true
        },
        createdBy: {
          label: 'Created By',
          value: (row) => `${row.createdBy?.firstName || ''} ${row.createdBy?.lastName || ''}`.trim() || '-',
          isNumeric: false
        },
        department: {
          label: 'Department',
          value: (row) => row.department?.name || 'N/A',
          isNumeric: false
        },
        assignedTo: {
          label: 'Assigned To',
          value: (row) => `${row.assignedTo?.firstName || ''} ${row.assignedTo?.lastName || ''}`.trim() || '-',
          isNumeric: false
        },
        approver: {
          label: 'Approver',
          value: (row) => {
            // Only show approver for Need Approval status (frontend transformed value)
            if (row.statusDesc === 'Need Approval' && row.approver) {
              const approverName = `${row.approver?.firstName || ''} ${row.approver?.lastName || ''}`.trim();
              return approverName;
            }
            return '-';
          },
          isNumeric: false
        },
        createdAt: {
          label: 'Created At',
          value: (row) => formatDateDisplay(row.createdAt),
          isNumeric: false
        },
        updatedAt: {
          label: 'Updated At',
          value: (row) => formatDateDisplay(row.updatedAt),
          isNumeric: false
        },
        statusDesc: { label: 'Status Approval', value: (row) => row.statusDesc || 'Draft', isNumeric: false },
        statusProposalDesc: { label: 'Status Proposal', value: (row) => row.statusProposalDesc || 'Draft', isNumeric: false },
        projectStatus: { label: 'Project Status', value: (row) => row.projectStatus || 'Not Converted', isNumeric: false },
        proposalStage: { label: 'Proposal Stage', value: (row) => getProposalStageName(row), isNumeric: false },
        stageUpdatedAt: { label: 'Stage Updated', value: (row) => row.stageUpdatedAt ? formatDateDisplay(row.stageUpdatedAt) : '-', isNumeric: false }
      };

      const visibleColumnKeys = visibleColumns.length > 0 ? visibleColumns : columns.map(col => col.key);
      const exportColumns = visibleColumnKeys
        .filter(key => columnExportMap[key])
        .map(key => ({
          key,
          label: columnExportMap[key].label,
          value: columnExportMap[key].value,
          isNumeric: columnExportMap[key].isNumeric || false
        }));

      const excelData = dataToExport.map(row => {
        const rowData = { 'ID': row.id || '' };
        exportColumns.forEach(col => {
          rowData[col.label] = col.value(row);
        });
        return rowData;
      });

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      const numericColumnIndices = [];
      exportColumns.forEach((col, idx) => {
        if (col.isNumeric) {
          numericColumnIndices.push(idx + 1);
        }
      });

      const getCurrencyFormat = (currencyCode) => {
        switch (currencyCode) {
          case 'USD': return '"$"#,##0';
          case 'EUR': return '#,##0" €"';
          case 'JPY': return '¥#,##0';
          case 'SGD': return '"S$"#,##0';
          case 'GBP': return '"£"#,##0';
          case 'AUD': return '"A$"#,##0';
          case 'IDR': return '"Rp"#,##0';
          default: return '#,##0';
        }
      };

      for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) {
        const dataRowIndex = rowNum - range.s.r - 1;
        const rowData = dataToExport[dataRowIndex];
        const currencyCode = rowData?.displayCurrencyCode || rowData?.currencyCode || 'IDR';
        const currencyFormat = getCurrencyFormat(currencyCode);

        numericColumnIndices.forEach(colIndex => {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colIndex });
          const cell = worksheet[cellAddress];
          if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
            cell.t = 'n';
            cell.z = currencyFormat;
          }
        });
      }

      const widthMap = {
        'Proposal': 22,
        'Client': 28,
        'Proposal Date': 16,
        'Valid Until': 16,
        'Partner Type': 18,
        'Amount': 20,
        'Created By': 18,
        'Department': 18,
        'Assigned To': 18,
        'Approver': 16,
        'Created At': 16,
        'Updated At': 16,
        'Status Approval': 18,
        'Status Proposal': 18,
        'Project Status': 18,
        'Proposal Stage': 20,
        'Stage Updated': 16
      };

      const columnWidths = [{ wch: 10 }];
      exportColumns.forEach(col => {
        columnWidths.push({ wch: widthMap[col.label] || 15 });
      });
      worksheet['!cols'] = columnWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Proposals');

      // Generate file name with timestamp
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `proposals_export_${timestamp}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, fileName);

      if (selectedRows && selectedRows.length > 0) {
        showAlert('success', `${selectedRows.length} selected proposal(s) exported to Excel successfully!`);
      } else if (hasActiveFilters()) {
        showAlert('success', `${dataToExport.length} filtered proposal(s) exported to Excel successfully!`);
      } else {
        showAlert('success', 'All proposals exported to Excel successfully!');
      }
    } catch (e) {
      console.error('Export Excel failed:', e);
      showAlert('danger', 'Failed to export proposals to Excel. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Rejected': 'danger',
      'Draft': 'secondary',
      'Need Approval': 'primary',
      'Revision': 'warning',
      'Approved': 'success',
    };
    return `badge badge-outline text-${colors[status] || 'secondary'}`;
  };

  const getProposalStatusBadge = (status) => {
    const colors = {
      'Draft': 'secondary',
      'Send to Client': 'primary',
      'Feedback': 'warning',
      'Accepted': 'success',
      'Rejected': 'danger',
    };
    return `badge badge-outline text-${colors[status] || 'secondary'}`;
  };

  const getProjectStatusBadge = (status) => {
    const colors = {
      'Converted': 'success',
      'Not Converted': 'orange'
    };
    return `badge badge-outline text-${colors[status] || 'secondary'}`;
  };

  const getProposalStageBadge = (stage) => {
    const colors = {
      '01. Negotiation': 'warning',
      '02. Close Won M1': 'info',
      '03. Close Won M2': 'primary',
      '04. Close Won M3': 'primary',
      '05. WON': 'success',
      '06. LOST': 'danger',
      'Not Set': 'secondary'
    };
    return `badge badge-outline text-${colors[stage] || 'secondary'}`;
  };

  const handleConvertToProject = async (proposal) => {
    // ✅ TAMBAHAN: Validasi Status Proposal harus 'Accepted' (kode 3)
    const currentStatusProposal = proposal?.statusProposal;
    const currentStatusProposalDesc = proposal?.statusProposalDesc;

    if (currentStatusProposal !== 3) {
      showAlert('warning', `Cannot convert to project: Proposal status must be 'Accepted' (current status: ${currentStatusProposalDesc || currentStatusProposal}). Only proposals with 'Accepted' status can be converted to projects.`);
      return;
    }

    // ✅ TAMBAHAN: Validasi NPWP - Check if company has NPWP (Tax Number)
    const companyId = proposal.companyId || proposal.company_id;
    if (companyId) {
      try {
        const companyData = await fetchCompanyData(companyId);
        setCompanyData(companyData);

        // Check if NPWP is empty or null
        const npwp = companyData?.npwp || companyData?.taxNumber || companyData?.companyTaxNumber;
        if (!npwp || npwp.trim() === '') {
          // Show NPWP validation modal instead of proceeding
          setItemToConvert(proposal);
          setNpwpValidationCompanyId(companyId);
          setNpwpInputValue('');
          setShowNpwpValidationModal(true);
          return;
        }
      } catch (error) {
        console.error('Error fetching company data for NPWP validation:', error);
        // If we can't fetch company data, show warning but allow conversion
        showAlert('warning', 'Could not verify company tax information. Proceeding with conversion...');
      }
    }

    setItemToConvert(proposal);
    setShowConvertModal(true);
  };

  const confirmConvert = async () => {
    // ✅ PERBAIKAN: Prevent concurrent calls
    if (isConverting) {
      console.warn('Convert already in progress, ignoring duplicate call');
      return;
    }

    try {
      setIsConverting(true);
      setLoading(true);

      // Pastikan kita punya proposalId yang benar
      let proposalId = itemToConvert?.id;
      if (!proposalId && itemToConvert?.leadId) {
        try {
          const resId = await api.get(`/crm/proposals/id-by-lead/${itemToConvert.leadId}`);
          proposalId = typeof resId.data === 'object' ? resId.data.id : resId.data;
        } catch (e) {
          console.error('Failed to resolve proposalId by leadId:', e);
        }
      }

      if (!proposalId) {
        showAlert('danger', 'Cannot convert: proposalId not found.');
        return;
      }

      // ✅ TAMBAHAN: Fetch proposal detail untuk mendapatkan data lengkap termasuk proposal_sections
      let proposalDetailData = null;
      try {
        const detailRes = await api.get(`/crm/proposals/detail/lead/${itemToConvert.leadId}`);
        proposalDetailData = detailRes.data;
      } catch (detailError) {
        console.error('Failed to fetch proposal detail:', detailError);
        showAlert('danger', 'Cannot convert: Failed to fetch proposal details.');
        return;
      }

      // ✅ TAMBAHAN: Validasi proposal_sections
      if (!proposalDetailData?.proposal_sections || proposalDetailData.proposal_sections.length === 0) {
        showAlert('danger', 'Cannot convert: No proposal sections found.');
        return;
      }

      // ✅ PERBAIKAN: Build payload dengan data yang lebih lengkap (sama seperti DetailProposal)
      const payload = {
        leadId: proposalDetailData?.leadId || itemToConvert.leadId,
        companyName: proposalDetailData?.client?.name || itemToConvert.companyName || itemToConvert.client,
        projectDate: proposalDetailData?.dates?.proposal_date || itemToConvert.proposalDate || null,
        validUntil: proposalDetailData?.dates?.valid_until || itemToConvert.validUntil || null,
        partnerType: proposalDetailData?.partnerType || itemToConvert.partnerType || null,
        grandTotal: proposalDetailData?.grandTotal ?? itemToConvert.grandTotal ?? itemToConvert.amount ?? 0,
        statusDesc: 'Draft',
        status: 1,
        // ✅ TAMBAHAN: Template dan assignment info
        projectTemplateId: proposalDetailData?.template?.id || null,
        assignedTo: proposalDetailData?.assigned_to?.name || null,
        team: proposalDetailData?.assigned_to?.team || null,
        // ✅ TAMBAHAN: Audit methodology fields dari proposal
        auditMethodology: proposalDetailData?.auditMethodology || null,
        auditFlaggingType: proposalDetailData?.auditFlaggingType || null,
        multiSite: proposalDetailData?.multiSite || false,
        siteAddress: proposalDetailData?.siteAddress || null,
        // ✅ PERBAIKAN: Project services mapping dari proposal sections dengan ID sebagai number
        projectServices: proposalDetailData?.proposal_sections?.map((section, sectionIdx) => {
          const mappedSection = {
            serviceId: parseInt(section.serviceId) || null, // Convert ke number
            serviceName: section.type || 'Service',
            standards: section.services?.map((service, serviceIdx) => {
              // ✅ IMPROVED: Better ID extraction with proper null checks
              // Priority: serviceItemId (new field) > service_item_id > fallback to child > default
              let parentServiceItemId = null;
              if (service.serviceItemId) {
                parentServiceItemId = parseInt(service.serviceItemId);
              } else if (service.service_item_id) {
                parentServiceItemId = parseInt(service.service_item_id);
              } else if (service.serviceItem?.id) {
                parentServiceItemId = parseInt(service.serviceItem.id);
              } else if (service.service_item?.id) {
                parentServiceItemId = parseInt(service.service_item.id);
              }

              // ✅ IMPROVED: If parent doesn't have serviceItemId, try to get it from the first child (fallback)
              if (!parentServiceItemId && service.sub_services && service.sub_services.length > 0) {
                const firstChild = service.sub_services[0];
                if (firstChild.serviceItemId) {
                  parentServiceItemId = parseInt(firstChild.serviceItemId);
                } else if (firstChild.service_item_id) {
                  parentServiceItemId = parseInt(firstChild.service_item_id);
                }
                if (parentServiceItemId) {
                  console.warn('⚠️ Using child serviceItemId for parent (parent should have its own):', parentServiceItemId);
                }
              }

              // ✅ IMPROVED: Skip this service if serviceItemId is missing (backend will reject invalid FK)
              if (!parentServiceItemId) {
                console.warn('⚠️ Skipping service - missing serviceItemId');
                return null; // Will be filtered out
              }

              // ✅ IMPROVED: Better productCategoryId extraction
              let parentProductCategoryId = null;
              if (service.productCategoryId) {
                parentProductCategoryId = parseInt(service.productCategoryId);
              } else if (service.product_category_id) {
                parentProductCategoryId = parseInt(service.product_category_id);
              }

              if (!parentProductCategoryId) {
                console.warn('⚠️ Skipping service - missing parentProductCategoryId');
                return null;
              }

              // ✅ IMPROVED: Better standardId extraction
              let standardId = null;
              if (service.standardId) {
                standardId = parseInt(service.standardId);
              }

              if (!standardId) {
                console.warn('⚠️ Skipping service - missing standardId');
                return null;
              }

              const mappedService = {
                standardId: standardId, // ✅ IMPROVED: Use extracted standardId
                standardName: service.standard || 'Standard',
                productCategoryId: parentProductCategoryId, // ✅ IMPROVED: Use extracted productCategoryId
                productCategoryName: service.product_type || service.productCategoryName || 'Product Category',
                serviceItemId: parentServiceItemId, // ✅ IMPROVED: Use the fixed parentServiceItemId
                serviceItemName: service.service_item || service.serviceItemName || 'Service Item',
                yearType: service.yearType || 0,
                selectedYear: service.selectedYear || new Date().getFullYear(),
                qty: service.qty || service.quantity || 1,
                unit: service.unit || 'Unit',
                unitPrice: service.unitPrice || 0,
                workflowIdSystem: service.workflowIdSystem || null,
                workflowIdChoice: service.workflowIdChoice || null,
                proposeDate: service.proposeDate || '', // ✅ TAMBAHAN: Include proposeDate in payload
                scope: service.scope || '',
                // Links the created project standard back to this proposal standard row -- EA
                // Code is read/edited live through this link (proposal_standard_items is the
                // single source of truth), never copied onto the project standard itself.
                proposalStandardItemId: service.proposalStandardItemId || null,
                // ✅ PERBAIKAN: Children mapping - filter out invalid entries, use null for empty
                children: (service.sub_services && service.sub_services.length > 0)
                  ? service.sub_services.map((subService, subIdx) => {
                      // Extract IDs for child - skip if serviceItemId missing
                      let childServiceItemId = null;
                      if (subService.serviceItemId) {
                        childServiceItemId = parseInt(subService.serviceItemId);
                      } else if (subService.service_item_id) {
                        childServiceItemId = parseInt(subService.service_item_id);
                      } else if (subService.id) {
                        childServiceItemId = parseInt(subService.id);
                      }

                      if (!childServiceItemId) {
                        console.warn('⚠️ Skipping child - missing serviceItemId');
                        return null;
                      }

                      let childProductCategoryId = null;
                      if (subService.productCategoryId) {
                        childProductCategoryId = parseInt(subService.productCategoryId);
                      } else {
                        childProductCategoryId = parentProductCategoryId; // Use parent's ID as fallback
                      }

                      return {
                        standardId: standardId, // ✅ IMPROVED: Use parent's standardId
                        standardName: subService.standard || service.standard || 'Standard',
                        productCategoryId: childProductCategoryId, // ✅ IMPROVED: Use extracted child productCategoryId
                        productCategoryName: subService.product_type || subService.productCategoryName || service.product_type || 'Product Category',
                        serviceItemId: childServiceItemId, // ✅ IMPROVED: Use extracted child serviceItemId
                        serviceItemName: subService.service_item || subService.serviceItemName || 'Service Item',
                        yearType: subService.yearType || 0,
                        selectedYear: subService.selectedYear || new Date().getFullYear(),
                        qty: subService.qty || subService.quantity || 1,
                        unit: subService.unit || 'Unit',
                        unitPrice: subService.unitPrice || 0,
                        workflowIdSystem: subService.workflowIdSystem || null,
                        workflowIdChoice: subService.workflowIdChoice || null,
                        proposeDate: subService.proposeDate || '', // ✅ TAMBAHAN: Include proposeDate for children
                        scope: subService.scope || service.scope || '',
                        proposalStandardItemId: subService.proposalStandardItemId || null,
                      };
                    }).filter(Boolean) // Remove null entries
                  : null // Use null instead of empty array for backend compatibility
              };
              return mappedService;
            }).filter(Boolean) // Remove null services
          };
          return mappedSection;
        }).filter(Boolean) // Remove null sections
      };

      // ✅ TAMBAHAN: Validasi projectServices sebelum dikirim
      if (!payload.projectServices || payload.projectServices.length === 0) {
        showAlert('danger', 'Cannot convert: No project services found in payload.');
        return;
      }

      // ✅ PERBAIKAN: Tambahkan fallback values untuk service yang tidak memiliki serviceId
      payload.projectServices = payload.projectServices.map((service, idx) => {
        // Jika serviceId tidak ada, gunakan fallback berdasarkan serviceName
        if (!service.serviceId || service.serviceId === null || service.serviceId === undefined) {
          if (service.serviceName) {
            // Mapping berdasarkan serviceName
            const serviceNameMapping = {
              'Training': 1,
              'Certification Program': 2,
              'Certification': 2,
              'Audit': 3,
              'Tes Services': 7,
              'ISCC': 8
            };
            service.serviceId = serviceNameMapping[service.serviceName] || 1; // Default ke 1
          } else {
            service.serviceId = 1; // Default fallback
          }
        }

        // Jika serviceName tidak ada, gunakan fallback
        if (!service.serviceName || service.serviceName.trim() === '') {
          service.serviceName = 'Service';
        }

        return service;
      });

      const res = await api.post(`/crm/projects/convert-from-proposal/${proposalId}`, payload);

      // ✅ TAMBAHAN: Update proposal stage to WON if not already WON
      const leadIdForDetail = res?.data?.leadId ?? itemToConvert.leadId;
      const currentProposalStageId = itemToConvert?.proposalStageId || proposalDetailData?.proposalStageId;
      
      // Find WON stage from proposal stages
      const wonStage = filterOptions.proposalStages?.find(stage => 
        stage.name && stage.name.toUpperCase().includes('WON')
      );
      
      // Check if current stage is already WON
      const isAlreadyWon = wonStage && currentProposalStageId && 
        parseInt(currentProposalStageId) === parseInt(wonStage.id);
      
      // Update stage to WON if not already WON
      if (wonStage && !isAlreadyWon && leadIdForDetail) {
        try {
          await api.put(`/crm/proposals/${leadIdForDetail}/update-stage`, {
            proposalStageId: wonStage.id
          });
        } catch (stageError) {
          console.warn('⚠️ Failed to update proposal stage to WON:', stageError);
          // Don't block navigation if stage update fails
        }
      }

      // ✅ PERBAIKAN: Update local state to mark as converted
      setProposals(prev =>
        prev.map(p => (p.id === itemToConvert.id || p.id === proposalId ? { ...p, projectStatus: 'Converted' } : p))
      );
      setFilteredProposals(prev =>
        prev.map(p => (p.id === itemToConvert.id || p.id === proposalId ? { ...p, projectStatus: 'Converted' } : p))
      );

      const alreadyExists = res?.data?.alreadyExists;

      if (leadIdForDetail) {
        if (alreadyExists) {
          showAlert('info', `Project already exists for proposal ${itemToConvert.proposal}. Redirecting to project detail...`);
        } else {
          showAlert('success', `Proposal ${itemToConvert.proposal} converted to project successfully`);
        }
        navigate(`/projects/detail/${leadIdForDetail}`);
      } else {
        showAlert('warning', 'Converted, but cannot navigate: leadId not returned.');
      }
    } catch (error) {
      console.error('Error converting proposal to project:', error);

      if (error.response?.data?.requiresAuditMethodology) {
        const leadIdForEdit = itemToConvert?.leadId;
        showAlert('warning', 'Audit Methodology belum diisi. Mengalihkan ke Edit Proposal...');
        if (leadIdForEdit) {
          setTimeout(() => navigate(`/proposals/edit/${leadIdForEdit}`), 1500);
        }
        return;
      }

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert proposal to project';

      console.error('Detailed convert to project error (List):', {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
      });

      showAlert('danger', `Convert to project failed: ${errorMessage}`);
    } finally {
      setIsConverting(false);
      setLoading(false);
      setShowConvertModal(false);
      setItemToConvert(null);
    }
  };

  const columns = [
    {
      key: 'proposal',
      label: 'Proposal',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.proposal || item.proposalCode || '',
      render: (value, row) => (
        <a
          href={`/proposals/detail/${row.leadId}`}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            navigate(`/proposals/detail/${row.leadId}`);
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          className="text-primary text-decoration-none text-truncate d-inline-block"
          style={{ cursor: 'pointer', maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </a>
      )
    },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.client || item.companyName || '',
      render: (value, item) => (
        <a
          href={(item.companyId || item.company_id || item.company?.id || item.clientId || item.client_id || item.lead?.companyId || item.lead?.company_id) ? `/clients/${item.companyId || item.company_id || item.company?.id || item.clientId || item.client_id || item.lead?.companyId || item.lead?.company_id}` : '#'}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{
            cursor: 'pointer',
            maxWidth: '200px',
            width: '200px'
          }}
          onClick={(e) => {
            // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return; // Let browser handle it
            }
            e.preventDefault();
            const companyId = item.companyId || item.company_id || item.company?.id || item.clientId || item.client_id || item.lead?.companyId || item.lead?.company_id;
            if (companyId) {
              navigate(`/clients/${companyId}`);
            }
          }}
          onAuxClick={(e) => {
            // Handle middle mouse button click
            if (e.button === 1) {
              return; // Let browser handle it
            }
          }}
          title={value}
          aria-label={`View client details for ${value}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const companyId = item.companyId || item.company_id || item.company?.id || item.clientId || item.client_id || item.lead?.companyId || item.lead?.company_id;
              if (companyId) {
                navigate(`/clients/${companyId}`);
              }
            }
          }}
        >
          {value}
        </a>
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      sortable: true,
      className: 'text-truncate',
      render: (value, item) => {
        // Company tags (blue)
        const companyRaw = item.tagging ?? value ?? '';
        const companyTags = typeof companyRaw === 'string'
          ? companyRaw.split(',').map(s => s.trim()).filter(Boolean)
          : [];

        // Proposal-specific tags (light green)
        const proposalRaw = item.proposalTagging ?? item.proposal_tagging ?? '';
        const proposalTags = typeof proposalRaw === 'string'
          ? proposalRaw.split(',').map(s => s.trim()).filter(Boolean)
          : [];

        if (companyTags.length === 0 && proposalTags.length === 0) {
          return <span className="text-muted">-</span>;
        }
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '200px' }}>
            {companyTags.map((tag, i) => (
              <span
                key={`c-${i}`}
                className="badge bg-blue-lt text-wrap"
                title={`Company tag: ${tag}`}
                style={{ fontSize: '0.7rem' }}
              >
                {tag}
              </span>
            ))}
            {proposalTags.map((tag, i) => (
              <span
                key={`p-${i}`}
                className="badge bg-yellow-lt text-wrap"
                title={`Proposal tag: ${tag}`}
                style={{fontSize: '0.7rem',}}
              >
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'proposalDate',
      label: 'Proposal Date',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.proposalDate || '',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.validUntil || '',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'partnerType',
      label: 'Partner Type',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    {
      key: 'sector',
      label: 'Sector',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.sectorName || item.sector || '',
      render: (value, item) => {
        const sectorName = item.sectorName || item.sector || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={sectorName}
          >
            {sectorName}
          </span>
        );
      }
    },
    {
      key: 'service',
      label: 'Service',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const services = (item.serviceNames || '').split(',').map(s => s.trim()).filter(Boolean);
        if (services.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {services.map((svc, i) => (
              <span
                key={i}
                className="badge bg-green-lt text-wrap"
                title={svc}
                style={{ fontSize: '0.7rem' }}
              >
                {svc}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'standard',
      label: 'Standard',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const standards = (item.standards || '').split(',').map(s => s.trim()).filter(Boolean);
        if (standards.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {standards.map((std, i) => (
              <span
                key={i}
                className="badge bg-azure-lt text-wrap"
                title={std}
                style={{ fontSize: '0.7rem' }}
              >
                {std}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => parseFloat(item.amount) || parseFloat(item.grandTotal) || 0,
      render: (value, item) => {
        // ✅ value here is already IDR-converted (backend resolveProposalAmountFromMap
        // multiplies grandTotal by exchangeRateAtCreation) — always format as IDR,
        // never with the proposal's original displayCurrencyCode (e.g. USD).
        const formattedValue = formatAmount(value, 'IDR');
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={formattedValue}
          >
            {formattedValue}
          </span>
        );
      }
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.createdBy?.firstName || ''} ${item.createdBy?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'branch',
      label: 'Branch',
      sortable: false,
      className: 'text-truncate',
      render: (value, item) => {
        const branchName = item.branchName || 'N/A';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={branchName}
          >
            {branchName}
          </span>
        );
      }
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.department?.name || 'N/A',
      render: (value, item) => {
        const deptName = item.department?.name || 'N/A';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={deptName}
          >
            {deptName}
          </span>
        );
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim(),
      render: (value, item) => {
        const fullName = `${item.assignedTo?.firstName || ''} ${item.assignedTo?.lastName || ''}`.trim();
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={fullName}
          >
            {fullName}
          </span>
        );
      }
    },
    {
      key: 'approver',
      label: 'Approver',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => {
        // Only show approver for Need Approval status (frontend transformed value)
        if (item.statusDesc === 'Need Approval' && item.approver) {
          return `${item.approver?.firstName || ''} ${item.approver?.lastName || ''}`.trim();
        }
        return '';
      },
      render: (value, item) => {
        // Only show approver for Need Approval status (frontend transformed value)
        if (item.statusDesc === 'Need Approval' && item.approver) {
          const fullName = `${item.approver?.firstName || ''} ${item.approver?.lastName || ''}`.trim();
          return (
            <span
              className="text-truncate d-inline-block"
              style={{ maxWidth: '140px', width: '140px' }}
              title={fullName}
            >
              {fullName}
            </span>
          );
        }
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title=""
          >
            -
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.createdAt || '',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.updatedAt || '',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'statusDesc',
      label: 'Status Approval',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        const statusValue = value || 'Draft';
        return (
          <span
            className={`${getStatusBadge(statusValue)} text-truncate d-inline-block`}
            style={{ maxWidth: '120px', width: '120px' }}
            title={statusValue}
          >
            {statusValue}
          </span>
        );
      }
    },
    {
      key: 'statusProposalDesc',
      label: 'Status Proposal',
      sortable: true,
      className: 'text-truncate',
      render: (value, row) => {
        // Use statusProposalDesc from API response
        const proposalStatus = value || row.statusProposalDesc || 'Draft';
        return (
          <span
            className={`${getProposalStatusBadge(proposalStatus)} text-truncate d-inline-block`}
            style={{ maxWidth: '120px', width: '120px' }}
            title={proposalStatus}
          >
            {proposalStatus}
          </span>
        );
      }
    },
    {
      key: 'projectStatus',
      label: 'Project Status',
      sortable: true,
      className: 'text-truncate',
      render: (value, row) => {
        const projectStatus = value || row.projectStatus || 'Not Converted';
        return (
          <span
            className={`${getProjectStatusBadge(projectStatus)} text-truncate d-inline-block`}
            style={{ maxWidth: '120px', width: '120px' }}
            title={projectStatus}
          >
            {projectStatus}
          </span>
        );
      }
    },
    {
      key: 'proposalStage',
      label: 'Proposal Stage',
      sortable: true,
      className: 'text-truncate',
      sortValue: (row) => {
        const id = row?.proposalStageId;
        const found = id != null ? (filterOptions?.proposalStages || []).find(ps => ps.id === parseInt(id)) : null;
        return found?.name || row.proposalStage || 'Not Set';
      },
      render: (value, row) => {
        // Tampilkan nama stage dari tabel proposal_stages berdasarkan ID
        const id = row?.proposalStageId;
        const found = id != null ? (filterOptions?.proposalStages || []).find(ps => ps.id === parseInt(id)) : null;
        const proposalStageName = found?.name || value || row.proposalStage || 'Not Set';
        return (
          <span
            className={`${getProposalStageBadge(proposalStageName)} text-truncate d-inline-block`}
            style={{ maxWidth: '140px', width: '140px' }}
            title={proposalStageName}
          >
            {proposalStageName}
          </span>
        );
      }
    },
    {
      key: 'stageUpdatedAt',
      label: 'Stage Updated',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      sortValue: (item) => item.stageUpdatedAt || '',
      render: (value) => {
        if (!value) return '-';
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    }
  ];

  useEffect(() => {
    if (columns.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(columns.map(col => col.key));
    }
  }, [columns.length, visibleColumns.length]);

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {/* Confirmation Modal for Delete */}
      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Item"
          message={`Are you sure you want to delete this? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
        />
      )}

      {/* Confirmation Modal for Convert to Project */}
      {showConvertModal && (
        <ConfirmationModal
          isOpen={showConvertModal}
          onClose={() => {
            if (!isConverting) {
              setShowConvertModal(false);
              setItemToConvert(null);
            }
          }}
          onConfirm={confirmConvert}
          title="Convert to Project"
          message={`Are you sure you want to convert this proposal to a project?`}
          confirmText={isConverting ? "Converting..." : "Convert"}
          cancelText="Cancel"
          confirmVariant="primary"
          isLoading={isConverting}
        />
      )}

      {/* NPWP Validation Modal - now allows filling NPWP inline instead of redirecting to Edit Company */}
      {showNpwpValidationModal && (
        <Modal
          isOpen={showNpwpValidationModal}
          onClose={() => {
            setShowNpwpValidationModal(false);
            setItemToConvert(null);
            setCompanyData(null);
            setNpwpValidationCompanyId(null);
            setNpwpInputValue('');
          }}
          title="Cannot Convert to Project"
          size="md"
        >
          <div className="modal-body">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <div className="alert-icon me-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon icon-2">
                  <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                  <path d="M12 8v4"></path>
                  <path d="M12 16h.01"></path>
                </svg>
              </div>
              <div>
                To proceed with converting to project, the Company Tax Number (NPWP) must be filled in.
                <br />
                <strong>Company: {companyData?.name || itemToConvert?.companyName}</strong>
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label">Company Tax Number (NPWP)</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter NPWP"
                value={npwpInputValue}
                onChange={(e) => setNpwpInputValue(e.target.value)}
                disabled={isSavingNpwp}
                autoFocus
              />
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-info"
              disabled={isSavingNpwp}
              onClick={() => {
                setShowNpwpValidationModal(false);
                setItemToConvert(null);
                setCompanyData(null);
                setNpwpValidationCompanyId(null);
                setNpwpInputValue('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isSavingNpwp}
              onClick={handleSaveNpwpAndContinue}
            >
              {isSavingNpwp ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                'Save & Continue'
              )}
            </button>
          </div>
        </Modal>
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          {/* <h3 className="page-subtitle m-0">Overview</h3> */}
          <h2 className="page-title">Dashboard Proposals</h2>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  {/* <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-overview" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">Overview</a>
                  </li> */}
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="false" tabIndex="-1" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  {/* <div className="tab-pane active show" id="tabs-overview" role="tabpanel">
                    <OverviewView stats={stats} />
                  </div> */}
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      paginationTopContent={
                        <div className="row">
                          <div className="col-12">
                            {/* Page-specific totals row */}
                            <div className="row mb-3">
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total records for this page:</strong>
                                  <span>{filteredProposals.length}</span>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total Amount (This Page):</strong>
                                  <span>{formatCurrency(totalSums.pageTotal, proposalCurrencyCode)}</span>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total Records:</strong>
                                  <span>{pagination.totalElements}</span>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="d-flex flex-column">
                                  <strong>Total Amount:</strong>
                                  <span>{formatCurrency(totalSums.totalAmount, proposalCurrencyCode)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      data={filteredProposals}
                      columns={columns}
                      onView={(item) => navigate(`/proposals/view/${item.leadId}`)}
                      onEdit={can('edit') ? handleEditLead : undefined}
                      onDelete={can('delete') ? handleDeleteLead : undefined}
                      isEditDisabled={(item) => {
                        // Only allow editing for draft, revision, and feedback statuses
                        const isDraft = item?.statusProposal === 1 || item?.statusProposalDesc === 'Draft';
                        const isRevision = item?.statusDesc === 'Revision' || item?.statusApproval === 'Revision';
                        const isFeedback = item?.statusProposal === 5 || item?.statusProposalDesc === 'Feedback';

                        // Disable edit for all statuses except draft, revision, and feedback
                        return !(isDraft || isRevision || isFeedback);
                      }}
                      additionalActions={(item) => {
                        const isConverted = item?.projectStatus === 'Converted';

                        return (
                          <button
                            className={`btn btn-sm ${isConverted ? 'btn-outline-success' : (item?.statusProposal === 3 ? 'btn-outline-primary' : 'btn-outline-secondary')}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isConverted) {
                                handleConvertToProject(item);
                              }
                            }}
                            disabled={isConverted || item?.statusProposal !== 3}
                            title={isConverted ?
                              'Already converted to project' :
                              (item?.statusProposal !== 3 ?
                                `Only proposals with 'Accepted' status can be converted to projects (current status: ${item?.statusProposalDesc || item?.statusProposal})` :
                                'Convert to Project'
                              )
                            }
                          >
                            {isConverted ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-checklist">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M9 11l3 3l8 -8" />
                                <path d="M20 12v6a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h9" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-right">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M5 12l14 0" />
                                <path d="M13 18l6 -6l-6 -6" />
                                <path d="M19 12l-6 -6" />
                              </svg>
                            )}
                          </button>
                        );
                      }}
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasProposals" activeCount={activeFilterCount} />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search Proposals"
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={can('export_data') ? handleExportExcel : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Proposals is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={proposalFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={loading || capabilitiesLoading}
                          activeCount={activeFilterCount}
                          filterTags={proposalFilterTags}
                          onRemoveFilter={handleProposalChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      checkboxSelection={true}
                      selectedRows={selectedRows}
                      onSelectRow={(id, isSelected) => {
                        if (isSelected) {
                          setSelectedRows(prev => [...prev, id]);
                        } else {
                          setSelectedRows(prev => prev.filter(rowId => rowId !== id));
                        }
                      }}
                      onSelectAllRows={(itemIds) => {
                        if (Array.isArray(itemIds) && itemIds.length > 0) {
                          setSelectedRows(prev => {
                            const newIds = itemIds.filter(id => !prev.includes(id));
                            return [...prev, ...newIds];
                          });
                        } else {
                          const currentPageIds = filteredProposals.map(item => item.id);
                          setSelectedRows(prev => prev.filter(id => !currentPageIds.includes(id)));
                        }
                      }}
                      visibleColumns={visibleColumns}
                      onVisibleColumnsChange={setVisibleColumns}
                      emptyStateMessage="No proposals found"
                      isLoading={loading || capabilitiesLoading}
                      onSortChange={handleSortChange}
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: handlePageChange,
                        onRowsPerPageChange: handleRowsPerPageChange
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasProposals"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={proposalFilterFields}
      />
    </div>
  );
};

export default Proposals; 
